import { Button, Flex, Tag } from 'antd';
import Table, { TableProps } from 'antd/es/table';
import {
  ColumnGroupType,
  ColumnsType,
  ColumnType,
  SortOrder,
} from 'antd/es/table/interface';
import classNames from 'classnames';
import { ReactComponent as SplitIcon } from '@Icons/approvals/split.svg';

import { TABLE_PAGE_DEFAULT } from '@Features/constants';
import { SortConfig } from '@Features/types/SortConfig';

import MPagination from '@Components/Base/Table/components/Pagination';
import {
  mapToCustomPageIndex,
  mapToDefaultPageIndex,
  prepareSortConfig,
} from '@Components/Base/Table/utils';
import { AnyObject } from 'antd/es/_util/type';
import Checkbox from 'antd/es/checkbox/Checkbox';
import { ReactNode, useCallback, useMemo, useRef } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useResizeObserver } from 'usehooks-ts';
import { Link } from 'react-router-dom';
import { FormikProps } from 'formik';
import styles from './PriceComparison.module.css';
import {
  ComparisonSheetSelectedItem,
  ComparisonSheetState,
  PriceComparisonDto,
  TenderSupplier,
  VendorOfferDto,
} from '../ComparisonSheet.types';
import MaterialRequestInfo from '../MaterialRequestInfo/MaterialRequestInfo';
import { isComparisonSheetRecordChecked, withSortIcon } from '../utils';
import { VendorQuantitySplitProps } from '../modals/VendorQuantitySplit/VendorQuantitySplit';

const TABLE_HEADER_HEIGHT = 104;

// such config is needed to exclude 'Click to cancel sorting' option
const SORT_DIRECTIONS: SortOrder[] = ['ascend', 'descend', 'ascend'];

/**
 * @todo rename dataSource to comparison table item def
 */
export interface MTableProps<RecordType = PriceComparisonDto>
  extends Omit<TableProps<RecordType>, 'columns'> {
  /**
   * @description replaces columns definition
   */
  vendors: Array<TenderSupplier>;
  // pagination @todo
  totalItems?: number;
  pageSize?: number;
  onChangePage?: (page: number) => void;
  onSort?: (sortConfig: SortConfig | null) => void;
  currentPage?: number;
  hidePagination?: boolean;
  formik: FormikProps<ComparisonSheetState>;
  selectionChange?: (newSelection: Array<ComparisonSheetSelectedItem>) => void;
  onSplit?: (record: VendorQuantitySplitProps['info']) => void;
  handleShowVendorDetails: (vendor: TenderSupplier) => void;
}

type MTable2ComponentType<T1 = TenderSupplier> = React.FC<MTableProps<T1>>;
type ComparisonSheetHydrated = Omit<
  PriceComparisonDto,
  'vendorOffers' | 'vendorOffers'
>;
type MappedComparisonSheetDto = ComparisonSheetHydrated & {
  vendorOffersById: {
    [k: number | string]: VendorOfferDto & { checked: boolean };
  };
  vendorOffers: Array<VendorOfferDto & { checked: boolean }>;
};

const quantityFormatter = new Intl.NumberFormat('ae-AE', {
  style: 'decimal',
  minimumFractionDigits: 3,
});

const renderAmountColumn = (
  vendorId: number
): Pick<ColumnType<MappedComparisonSheetDto>, 'onCell' | 'render'> => {
  return {
    onCell: (record: MappedComparisonSheetDto) => {
      const offer = record.vendorOffers.find((so) => so.vendorId === vendorId);
      const bestAmount = record.vendorOffers.reduce((acc, so) => {
        return Math.min(acc, Number(so.amount));
      }, Infinity);
      return {
        className: classNames({
          [styles['best-buy']]: bestAmount === Number(offer?.amount),
        }),
      };
    },
    render: (value: string | number, record: MappedComparisonSheetDto) => {
      const offer = record.vendorOffers.find(
        (so) => Number(so.vendorId) === vendorId
      );
      const bestAmount = record.vendorOffers.reduce((acc, so) => {
        return Math.min(acc, Number(so.amount));
      }, Infinity);
      return (
        <Flex vertical align="center">
          {bestAmount === Number(offer?.amount) && (
            <div className={styles.wrapperTag}>
              <Tag color="#71D1A4" className={styles.cheapest}>
                Cheapest
              </Tag>
            </div>
          )}
          <div>{value && quantityFormatter.format(Number(value))}</div>
        </Flex>
      );
    },
  };
};

const renderAvailColumn = (value: boolean): ReactNode => {
  return (value && 'Yes') || 'No';
};

const renderRateColumn = (
  vendorId: number,
  disabled: boolean,
  onSelectionChange: (
    materialId: number | string,
    supplierId: number | string,
    value: boolean,
    amount: number | string
  ) => void
): Pick<ColumnType<MappedComparisonSheetDto>, 'render'> => {
  return {
    render: (value: string | number, record: MappedComparisonSheetDto) => {
      const offer = record.vendorOffers.find(
        (so) => Number(so.vendorId) === vendorId
      );
      const chbxDisabled = !offer?.available || disabled;

      return (
        offer && (
          <Flex vertical gap="small">
            <Flex align="center" gap="small">
              <Checkbox
                checked={offer.checked}
                className={
                  disabled ? classNames(styles['checkbox-disabled']) : undefined
                }
                disabled={chbxDisabled}
                onChange={(v) =>
                  onSelectionChange(
                    record.matId as number,
                    offer.vendorId,
                    v.target.checked,
                    offer.amount
                  )
                }
              />
              {value && quantityFormatter.format(+value)}
            </Flex>
          </Flex>
        )
      );
    },
  };
};

const PriceComparison = (
  props: MTableProps<PriceComparisonDto>
): ReturnType<MTable2ComponentType<MappedComparisonSheetDto>> => {
  const {
    dataSource,
    totalItems,
    pageSize,
    onChangePage,
    onSort,
    currentPage,
    className,
    hidePagination = false,
    vendors,
    selectionChange,
    onSplit,
    formik,
    handleShowVendorDetails,
    ...restProps
  } = props;

  const mappedData: MappedComparisonSheetDto[] | undefined = dataSource?.map(
    (ds) => {
      return {
        ...ds,
        vendorOffersById: ds.vendorOffers.reduce((acc, so) => {
          return {
            ...acc,
            [so.vendorId]: {
              ...so,
              checked: isComparisonSheetRecordChecked(
                formik.values.selectedOffersIDs,
                ds.matId,
                so.vendorId
              ),
            },
          };
        }, {}),
        vendorOffers: ds.vendorOffers.map((so) => ({
          ...so,
          // @todo get rid of multiple checked flag evaluation
          checked: isComparisonSheetRecordChecked(
            formik.values.selectedOffersIDs,
            ds.matId,
            so.vendorId
          ),
        })),
      };
    }
  );

  const currentPageValue = mapToCustomPageIndex(
    currentPage || TABLE_PAGE_DEFAULT
  );

  const toggleAllChecked = useCallback(
    (vendorId: number | string, checked: boolean) => {
      const filteredSelection =
        formik.values.selectedOffersIDs?.filter(
          ({ vendorId: selectedVendorId }) => selectedVendorId !== vendorId
        ) || [];
      if (checked) {
        const newSelection: Array<ComparisonSheetSelectedItem> = [
          ...filteredSelection,
          ...(mappedData || []).map(
            (row) =>
              ({
                matId: row.matId,
                vendorId,
              }) as ComparisonSheetSelectedItem
          ),
        ];

        selectionChange?.(newSelection);
      } else {
        selectionChange?.(filteredSelection);
      }
    },
    [formik.values.selectedOffersIDs, mappedData, selectionChange]
  );

  const rateColumnHeader = useCallback(
    (supplierId: number | string) => {
      const allSelected = mappedData?.every(
        (row) => row?.vendorOffersById[supplierId]?.checked === true
      );
      return (
        <Flex align="center" gap="small">
          <Checkbox
            checked={allSelected}
            onClick={(e) => {
              e.stopPropagation();
              toggleAllChecked(supplierId, !allSelected);
            }}
            className={
              formik.values.disabledItems?.disableSelection
                ? classNames(styles['checkbox-disabled'])
                : undefined
            }
            disabled={formik.values.disabledItems?.disableSelection}
          />
          Rate
        </Flex>
      );
    },
    [
      formik.values.disabledItems?.disableSelection,
      mappedData,
      toggleAllChecked,
    ]
  );

  const getSplitInfo = useCallback(
    (record: MappedComparisonSheetDto) => {
      const selectedOffers =
        formik.values.selectedOffersIDs
          ?.map(({ matId, vendorId }) =>
            matId === record.matId ? vendorId : null
          )
          .filter((o) => o != null) || [];
      const offers = record!.vendorOffers
        .filter((offer) => selectedOffers.includes(offer.vendorId))
        .map((offer) => ({
          offer,
          vendor: vendors.find((v) => v.id === offer.vendorId)!,
        }));
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const splitInfo: VendorQuantitySplitProps['info'] = {
        material: record.material,
        offers,
      };

      return splitInfo;
    },
    [formik.values.selectedOffersIDs, vendors]
  );

  const columns: ColumnsType<MappedComparisonSheetDto> = useMemo(() => {
    return [
      {
        title: 'Materials',
        dataIndex: ['material'],
        key: 'name',
        width: 370,
        fixed: 'left',
        onHeaderCell: () => {
          return {
            className: classNames(styles['material-cell-header']),
          };
        },
        render: (value) => <MaterialRequestInfo value={value} />,
      },
      ...vendors.map(
        (s) =>
          ({
            title: (
              <Button
                type="link"
                onClick={() => handleShowVendorDetails(s)}
                className={styles['vendor-details']}
              >
                {s.name}
              </Button>
            ),
            children: (
              [
                {
                  title: rateColumnHeader(s.id),
                  dataIndex: ['vendorOffersById', s.id, 'rate'],
                  key: ['vendorOffersById', s.id, 'rate'].join('_'),
                  width: 120,
                  ...renderRateColumn(
                    s.id,
                    Boolean(formik.values.disabledItems?.disableSelection),
                    (rowId, supplierId, value) => {
                      if (value) {
                        const newSelection = [
                          ...(formik.values.selectedOffersIDs || []),
                          {
                            matId: rowId,
                            vendorId: supplierId,
                          } as ComparisonSheetSelectedItem,
                        ];
                        selectionChange?.(newSelection);
                      } else {
                        const newSelection =
                          formik.values.selectedOffersIDs?.filter(
                            ({ matId, vendorId }) =>
                              !(matId === rowId && supplierId === vendorId)
                          ) || [];
                        selectionChange?.(newSelection);
                      }
                    }
                  ),
                  sorter: (a, b) => {
                    const aValue = a.vendorOffers?.[s.id]?.rate;
                    const bValue = b.vendorOffers?.[s.id]?.rate;

                    // eslint-disable-next-line no-nested-ternary
                    return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
                  },
                },
                {
                  title: 'Amount',
                  dataIndex: ['vendorOffersById', s.id, 'amount'],
                  key: ['vendorOffers', s.id, 'amount'].join('_'),
                  width: 120,
                  ...renderAmountColumn(s.id),
                  sorter: true,
                },
                {
                  title: 'Available',
                  dataIndex: ['vendorOffersById', s.id, 'available'],
                  key: ['vendorOffers', s.id, 'available'].join('_'),
                  width: 150,
                  render: renderAvailColumn,
                  sorter: true,
                },
                {
                  title: 'Brand',
                  dataIndex: ['vendorOffersById', s.id, 'brand'],
                  key: ['vendorOffers', s.id, 'brand'].join('_'),
                  width: 170,
                  sorter: true,
                },
              ] satisfies ColumnsType<MappedComparisonSheetDto>
            ).map((c) => withSortIcon(c)),
          }) satisfies ColumnGroupType<MappedComparisonSheetDto>
      ),
      {
        title: 'Action',
        key: 'operation',
        fixed: 'right',
        width: 100,
        render: (
          _value: MappedComparisonSheetDto,
          record: MappedComparisonSheetDto
        ) => {
          const canBeSplitted =
            record.vendorOffers.filter((so) => so.checked)?.length >= 2 &&
            !formik.values.disabledItems?.disableSplit;

          return (
            <Link
              to="."
              onClick={() =>
                formik.values.splitMaterials.length && canBeSplitted
                  ? onSplit?.(getSplitInfo(record))
                  : undefined
              }
            >
              <Flex
                align="center"
                gap="small"
                className={
                  canBeSplitted
                    ? styles['action-active']
                    : styles['action-disabled']
                }
              >
                <SplitIcon />
                Split
              </Flex>
            </Link>
          );
        },
      },
    ] satisfies ColumnsType<MappedComparisonSheetDto>;
  }, [
    vendors,
    rateColumnHeader,
    formik.values.disabledItems?.disableSelection,
    formik.values.disabledItems?.disableSplit,
    formik.values.selectedOffersIDs,
    formik.values.splitMaterials.length,
    handleShowVendorDetails,
    selectionChange,
    onSplit,
    getSplitInfo,
  ]);

  const handleChange = (page: number): void => {
    const pageIndex = mapToDefaultPageIndex(page);
    onChangePage?.(pageIndex);
  };

  const containerRef = useRef<HTMLElement | null>(null);
  const { height = 0, width = 0 } = useResizeObserver({
    ref: containerRef,
    box: 'border-box',
  });

  return (
    <Flex
      vertical
      className={classNames(
        styles['price-comparison'],
        'trojan-table-container'
      )}
      ref={containerRef}
    >
      <Table<MappedComparisonSheetDto>
        {...(restProps as unknown as AnyObject)}
        className={classNames('trojan-table', className, 'table-flex')}
        sortDirections={SORT_DIRECTIONS}
        columns={columns}
        dataSource={mappedData}
        pagination={false}
        scroll={{ x: width, y: height - TABLE_HEADER_HEIGHT }}
        onChange={(_pagination, _filters, sorter, extra) => {
          if (extra.action === 'sort') {
            const sortConfig = prepareSortConfig(sorter);
            onSort?.(sortConfig);
          }
        }}
      />
      {!hidePagination && columns && mappedData && (
        <Flex className="trojan-table-pagination trojan-box-shadow trojan-table-pagination-box-shadow">
          <MPagination
            showQuickJumper
            pageSize={pageSize}
            showSizeChanger={false}
            current={currentPageValue}
            total={totalItems}
            onChange={handleChange}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default PriceComparison;
